import { useState, useEffect, useCallback } from "react";
import {
    Trash2,
    Eye,
    Search,
    X,
    RotateCcw,
    ChevronDown,
    ChevronRight,
} from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import ThemedTable from "../../components/ThemedTable";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import socket from "../../socket/socketClient";
import OrderDateFilter from "../../components/OrderDateFilter";
import ConfirmDialog from "../../components/ConfirmDialog";

const RetailOrders = () => {
    /* ─────────────── STATE ─────────────── */
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Status-tab filter – triggers immediately (no staging needed)
    const [statusFilter, setStatusFilter] = useState("");

    // Staged: what the user types / selects in the control bar
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Applied: committed to the API only when Search button is clicked
    const [appliedSearch, setAppliedSearch] = useState("");
    const [appliedFromDate, setAppliedFromDate] = useState("");
    const [appliedToDate, setAppliedToDate] = useState("");

    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        pending: 0,
        confirmed: 0,
        processing: 0,
        shipped: 0,
        out_for_delivery: 0,
        delivered: 0,
        completed: 0,
        cancelled: 0,
    });

    const [needsRefresh, setNeedsRefresh] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [confirmModal, setConfirmModal] = useState({ open: false, type: null, orderId: null });

    const inputClass =
        "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full text-slate-800";

    /* ─────────────── TOGGLE ROW ─────────────── */
    const toggleRow = (id) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    /* ─────────────── LOAD ORDERS ─────────────── */
    const loadOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append("page", page);
            params.append("limit", 200);
            if (statusFilter) params.append("status", statusFilter);
            if (appliedSearch) params.append("search", appliedSearch);
            if (appliedFromDate) params.append("from", appliedFromDate);
            if (appliedToDate) params.append("to", appliedToDate);

            const res = await apiClient.get(`/retail-orders?${params}`);
            setOrders(res.data?.orders || []);
            setTotalPages(res.data?.pagination?.pages || 1);
            if (res.data?.statusCounts) setStatusCounts(res.data.statusCounts);
        } catch (err) {
            console.error("Failed to load retail orders:", err);
            toast.error("Failed to load orders");
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, appliedSearch, appliedFromDate, appliedToDate]);

    // Single useEffect – fires whenever any of the above deps change
    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    /* ─────────────── REAL-TIME ─────────────── */
    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === "visible") loadOrders();
            else setNeedsRefresh(true);
        };
        socket.on("retail_order_created", refresh);
        socket.on("retail_order_updated", refresh);
        socket.on("retail_order_status_updated", refresh);
        return () => {
            socket.off("retail_order_created", refresh);
            socket.off("retail_order_updated", refresh);
            socket.off("retail_order_status_updated", refresh);
        };
    }, [loadOrders]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible" && needsRefresh) {
                loadOrders();
                setNeedsRefresh(false);
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [needsRefresh, loadOrders]);

    /* ─────────────── HANDLERS ─────────────── */
    const handleSearch = () => {
        setAppliedSearch(searchTerm);
        setAppliedFromDate(fromDate);
        setAppliedToDate(toDate);
        setPage(1);
    };

    const handleReset = () => {
        setSearchTerm("");
        setFromDate("");
        setToDate("");
        setAppliedSearch("");
        setAppliedFromDate("");
        setAppliedToDate("");
        setStatusFilter("");
        setPage(1);
    };

    const handleUpdateStatus = async (orderId, newStatus) => {
        setActionLoading({ id: orderId, action: 'status' });
        try {
            await apiClient.put(`/retail-orders/${orderId}/status`, {
                status: newStatus,
            });
            toast.success(`Order marked as ${newStatus}`);
            loadOrders();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update status");
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancelOrder = (orderId) => {
        setConfirmModal({ open: true, type: 'cancel', orderId });
    };

    const handleDelete = (id) => {
        setConfirmModal({ open: true, type: 'delete', orderId: id });
    };

    const handleConfirmAction = async () => {
        const { type, orderId } = confirmModal;
        setConfirmModal({ open: false, type: null, orderId: null });
        
        if (type === 'cancel') {
            setActionLoading({ id: orderId, action: 'cancel' });
            try {
                await apiClient.put(`/retail-orders/${orderId}/cancel`, {});
                toast.success("Order cancelled");
                loadOrders();
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to cancel order");
            } finally {
                setActionLoading(null);
            }
        } else if (type === 'delete') {
            try {
                await apiClient.delete(`/retail-orders/${orderId}`);
                toast.success("Order deleted");
                loadOrders();
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to delete order");
            }
        }
    };

    /* ─────────────── STATUS HELPERS ─────────────── */
    const STATUS_TRANSITIONS = {
        pending: {
            next: "confirmed",
            label: "Accept",
            cls: "bg-green-600  hover:bg-green-700",
        },
        confirmed: {
            next: "processing",
            label: "Process",
            cls: "bg-blue-600   hover:bg-blue-700",
        },
        processing: {
            next: "shipped",
            label: "Ship",
            cls: "bg-indigo-600 hover:bg-indigo-700",
        },
        shipped: {
            next: "out_for_delivery",
            label: "Out for Delivery",
            cls: "bg-cyan-600   hover:bg-cyan-700",
        },
        out_for_delivery: {
            next: "delivered",
            label: "Delivered",
            cls: "bg-green-600  hover:bg-green-700",
        },
        delivered: {
            next: "completed",
            label: "Complete",
            cls: "bg-emerald-600 hover:bg-emerald-700",
        },
    };

    const STATUS_COLORS = {
        pending: "bg-yellow-100  text-yellow-800",
        confirmed: "bg-blue-100    text-blue-800",
        processing: "bg-purple-100  text-purple-800",
        shipped: "bg-indigo-100  text-indigo-800",
        out_for_delivery: "bg-cyan-100    text-cyan-800",
        delivered: "bg-green-100   text-green-800",
        completed: "bg-emerald-100 text-emerald-800",
        cancelled: "bg-red-100     text-red-800",
    };

    const getStatusLabel = (s) => s?.replace(/_/g, " ").toUpperCase() ?? "";

    /* ─────────────── UI ─────────────── */
    return (
        <div className="">
            {/* ── HEADER ── */}
            <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                    <h1 className="text-xl font-bold text-slate-800">Retail Orders</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Mobile app orders and customer requests
                    </p>
                </div>
            </div>

            {/* ── FILTER BAR ── */}
            <div className="mb-0">
                <div className="flex flex-col sm:flex-row gap-2 w-full items-stretch p-4">
                    {/* SEARCH INPUT */}
                    <div className="relative flex-1">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <Search size={16} />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by ID, Customer, Phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            className={`${inputClass} pl-10 pr-10`}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {/* DATE FILTER */}
                    <div className="w-full sm:w-72">
                        <OrderDateFilter
                            fromDate={fromDate}
                            toDate={toDate}
                            onApply={(from, to) => {
                                setFromDate(from);
                                setToDate(to);
                            }}
                            onClear={() => {
                                setFromDate("");
                                setToDate("");
                            }}
                            className={inputClass}
                        />
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleSearch}
                            title="Search"
                            className="w-9 h-9 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors shadow-sm flex items-center justify-center shrink-0"
                        >
                            <Search size={18} />
                        </button>
                        <button
                            onClick={handleReset}
                            title="Reset all filters"
                            className="w-9 h-9 bg-[#172554] text-white rounded-md border border-[#172554] flex items-center justify-center shrink-0 hover:bg-[#1e3a8a] transition-colors"
                        >
                            <RotateCcw size={16} />
                        </button>
                    </div>
                </div>

                {/* ── STATUS TABS ── */}
                <div className="flex flex-wrap items-center gap-1.5 px-3 py-2.5 bg-blue-50 border-t border-slate-200">
                    {[
                        ["", "All"],
                        ["pending", "Pending"],
                        ["confirmed", "Confirmed"],
                        ["processing", "Processing"],
                        ["shipped", "Shipped"],
                        ["out_for_delivery", "Out Delivery"],
                        ["delivered", "Delivered"],
                        ["completed", "Completed"],
                        ["cancelled", "Cancelled"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => {
                                setStatusFilter(key);
                                setPage(1);
                            }}
                            className={`flex-shrink-0 ps-3 pe-2 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${statusFilter === key
                                    ? "bg-orange-500 text-white shadow-sm"
                                    : "bg-white text-slate-500 border border-slate-200 hover:border-orange-300 hover:text-orange-500"
                                }`}
                        >
                            {label}
                            <span
                                className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${statusFilter === key
                                        ? "bg-white/20 text-white"
                                        : "bg-slate-100 text-slate-500"
                                    }`}
                            >
                                {statusCounts[key || "all"]}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* ── TABLE ── */}
            <div className="border border-slate-200 rounded-none bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <ThemedTable className="text-left border-collapse w-full">
                        <thead>
                            <tr className="bg-white text-gray-700 border-b border-slate-200">
                                <th className="p-3 w-8"></th>
                                <th className="p-3 text-left whitespace-nowrap">
                                    Order Information
                                </th>
                                <th className="p-3 text-left whitespace-nowrap">
                                    Customer Details
                                </th>
                                <th className="p-3 text-left whitespace-nowrap">Amount</th>
                                <th className="p-3 text-left whitespace-nowrap">Status</th>
                                <th className="p-3 text-center whitespace-nowrap">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-800">
                            {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                    <tr
                                        key={i}
                                        className="border-b border-slate-100 animate-pulse"
                                    >
                                        {Array.from({ length: 6 }).map((_, j) => (
                                            <td key={j} className="p-3">
                                                <div className="h-4 bg-slate-100 rounded w-full max-w-[120px]" />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : orders.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="6"
                                        className="text-center py-20 text-slate-400 text-sm"
                                    >
                                        No retail orders found
                                    </td>
                                </tr>
                            ) : (
                                orders.map((order) => (
                                    /* Use React.Fragment with key to support sibling rows */
                                    <tr
                                        key={`fragment-${order._id}`}
                                        style={{ display: "contents" }}
                                    >
                                        {/* MAIN ROW */}
                                        <tr
                                            className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${expandedRows.has(order._id) ? "bg-blue-50/30" : ""
                                                }`}
                                            onClick={() => toggleRow(order._id)}
                                        >
                                            {/* Chevron */}
                                            <td className="p-3">
                                                <span className="text-slate-400 hover:text-blue-600 transition-colors">
                                                    {expandedRows.has(order._id) ? (
                                                        <ChevronDown size={16} />
                                                    ) : (
                                                        <ChevronRight size={16} />
                                                    )}
                                                </span>
                                            </td>

                                            {/* Order Info */}
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="font-bold text-slate-800 text-sm tracking-tight">
                                                        {order.orderId}
                                                    </span>
                                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium">
                                                        <span>
                                                            {new Date(order.createdAt).toLocaleDateString(
                                                                "en-IN",
                                                                {
                                                                    day: "2-digit",
                                                                    month: "2-digit",
                                                                    year: "numeric",
                                                                    timeZone: "Asia/Kolkata",
                                                                },
                                                            )}
                                                        </span>
                                                        <span className="text-slate-300">•</span>
                                                        <span>
                                                            {new Date(order.createdAt).toLocaleTimeString(
                                                                "en-IN",
                                                                {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                    hour12: true,
                                                                    timeZone: "Asia/Kolkata",
                                                                },
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Customer */}
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <div
                                                        className="max-w-[180px] truncate text-slate-700 font-semibold text-sm"
                                                        title={order.userName || order.mobileUserId?.name}
                                                    >
                                                        {order.userName ||
                                                            order.mobileUserId?.name ||
                                                            "N/A"}
                                                    </div>
                                                    <span className="text-[10px] text-slate-400 font-medium">
                                                        {order.userPhone}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="p-3 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-800 text-sm">
                                                        ₹{order.paymentDetails?.totalAmount || 0}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                                                        {order.paymentDetails?.paymentMethod || "COD"}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Status badge */}
                                            <td className="p-3 whitespace-nowrap">
                                                <span
                                                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_COLORS[order.status] ||
                                                        "bg-gray-100 text-gray-800"
                                                        }`}
                                                >
                                                    {getStatusLabel(order.status)}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-center gap-2 flex-wrap">
                                                    {/* Next-status progression button */}
                                                    {STATUS_TRANSITIONS[order.status] && (
                                                        <button
                                                            onClick={() =>
                                                                handleUpdateStatus(
                                                                    order._id,
                                                                    STATUS_TRANSITIONS[order.status].next,
                                                                )
                                                            }
                                                            disabled={actionLoading?.id === order._id && actionLoading?.action === 'status'}
                                                            className={`h-8 px-4 flex items-center justify-center text-[11px] font-bold uppercase tracking-wider text-white rounded-full transition-all shadow-sm ${STATUS_TRANSITIONS[order.status].cls} disabled:opacity-70`}
                                                            title={`Mark as ${STATUS_TRANSITIONS[order.status].label}`}
                                                        >
                                                            {actionLoading?.id === order._id && actionLoading?.action === 'status' ? (
                                                                <Loader size={14} inline />
                                                            ) : (
                                                                STATUS_TRANSITIONS[order.status].label
                                                            )}
                                                        </button>
                                                    )}

                                                    {/* Cancel button */}
                                                    {order.status !== "cancelled" &&
                                                        order.status !== "completed" && (
                                                            <button
                                                                onClick={() => handleCancelOrder(order._id)}
                                                                disabled={actionLoading?.id === order._id && actionLoading?.action === 'cancel'}
                                                                className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition-all shadow-sm disabled:opacity-70"
                                                                title="Cancel Order"
                                                            >
                                                                {actionLoading?.id === order._id && actionLoading?.action === 'cancel' ? (
                                                                    <Loader size={14} inline />
                                                                ) : (
                                                                    <X size={14} strokeWidth={3} />
                                                                )}
                                                            </button>
                                                        )}

                                                    {/* Delete button (temporarily hidden as requested) */}
                                                    {/* {order.status !== "completed" && (
                            <button
                              onClick={() => handleDelete(order._id)}
                              className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition-all shadow-sm"
                              title="Delete Order"
                            >
                              <Trash2 size={14} />
                            </button>
                          )} */}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* EXPANDED PRODUCT DETAIL ROW */}
                                        {expandedRows.has(order._id) && (
                                            <tr className="bg-white border-b border-slate-100">
                                                <td className="bg-slate-50/30" colSpan="6">
                                                    <div className="grid grid-cols-1 lg:grid-cols-3">
                                                        {/* LEFT COLUMN: Order Info Cards */}
                                                        <div className="flex flex-col">
                                                            {/* Customer & Delivery Card */}
                                                            <div className="bg-white p-3 rounded-none border border-slate-200/60">
                                                                <p className="text-[10px] text-slate-400 uppercase font-bold mb-2">
                                                                    Customer & Delivery
                                                                </p>
                                                                <div className="space-y-1.5 text-xs">
                                                                    <div className="flex justify-between items-start">
                                                                        <span className="text-slate-500">
                                                                            Name:
                                                                        </span>
                                                                        <span className="font-semibold text-slate-700 text-right">
                                                                            {order.userName ||
                                                                                order.mobileUserId?.name ||
                                                                                "N/A"}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-start">
                                                                        <span className="text-slate-500">
                                                                            Phone:
                                                                        </span>
                                                                        <span className="font-medium text-slate-700 text-right">
                                                                            {order.userPhone || "N/A"}
                                                                        </span>
                                                                    </div>
                                                                    {(order.userEmail ||
                                                                        order.mobileUserId?.email) && (
                                                                            <div className="flex justify-between items-start">
                                                                                <span className="text-slate-500">
                                                                                    Email:
                                                                                </span>
                                                                                <span className="font-medium text-slate-700 text-right">
                                                                                    {order.userEmail ||
                                                                                        order.mobileUserId?.email}
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                    {order.deliveryAddress && (
                                                                        <div className="mt-2 pt-2 border-t border-slate-100">
                                                                            <span className="text-slate-500 block mb-0.5">
                                                                                Shipping Address
                                                                                <span className="ml-1 uppercase text-[9px] bg-slate-100 px-1 py-0.5 rounded font-bold text-slate-600">
                                                                                    {order.deliveryAddress.addressType ||
                                                                                        "Home"}
                                                                                </span>
                                                                            </span>
                                                                            <span className="font-medium text-slate-700 block leading-relaxed">
                                                                                {[
                                                                                    order.deliveryAddress.name,
                                                                                    order.deliveryAddress.street,
                                                                                    order.deliveryAddress.city,
                                                                                    order.deliveryAddress.state,
                                                                                    order.deliveryAddress.pincode,
                                                                                ]
                                                                                    .filter(Boolean)
                                                                                    .join(", ")}
                                                                            </span>
                                                                            {order.deliveryAddress.phone &&
                                                                                order.deliveryAddress.phone !==
                                                                                order.userPhone && (
                                                                                    <span className="text-slate-500 mt-0.5 block">
                                                                                        Alt Phone:{" "}
                                                                                        {order.deliveryAddress.phone}
                                                                                    </span>
                                                                                )}
                                                                        </div>
                                                                    )}
                                                                    {order.orderNotes && (
                                                                        <div className="mt-2 pt-2 border-t border-slate-100">
                                                                            <span className="text-slate-500 block mb-0.5">
                                                                                Order Notes:
                                                                            </span>
                                                                            <span className="text-[11px] text-amber-800 italic bg-amber-50/50 p-1.5 rounded border border-amber-100 block">
                                                                                "{order.orderNotes}"
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Payment & Order Summary Card */}
                                                            <div className="bg-white p-3 rounded-none border border-slate-200/60">
                                                                <p className="text-[10px] text-slate-400 uppercase font-bold mb-2">
                                                                    Payment Details
                                                                </p>
                                                                <div className="space-y-1.5 text-xs">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-slate-500">
                                                                            Method:
                                                                        </span>
                                                                        <span className="font-bold text-slate-700 uppercase">
                                                                            {order.paymentDetails?.paymentMethod ||
                                                                                "COD"}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-slate-500">
                                                                            Status:
                                                                        </span>
                                                                        <span
                                                                            className={`font-bold uppercase ${order.paymentDetails?.paymentStatus ===
                                                                                    "paid"
                                                                                    ? "text-green-600"
                                                                                    : "text-amber-600"
                                                                                }`}
                                                                        >
                                                                            {order.paymentDetails?.paymentStatus ||
                                                                                "unpaid"}
                                                                        </span>
                                                                    </div>
                                                                    {order.paymentDetails?.transactionId && (
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-slate-500">
                                                                                Txn ID:
                                                                            </span>
                                                                            <span className="font-mono text-[10px] text-slate-700">
                                                                                {order.paymentDetails.transactionId}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-slate-500">
                                                                                Subtotal:
                                                                            </span>
                                                                            <span className="font-semibold text-slate-700">
                                                                                ₹
                                                                                {(
                                                                                    (order.paymentDetails?.totalAmount ||
                                                                                        0) +
                                                                                    (order.paymentDetails?.discount || 0)
                                                                                ).toLocaleString()}
                                                                            </span>
                                                                        </div>

                                                                        {order.appliedOffer && (
                                                                            <div className="flex justify-between items-center">
                                                                                <span className="text-slate-500">
                                                                                    Offer Applied:
                                                                                </span>
                                                                                <span className="font-semibold text-blue-600 text-[10px] uppercase bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                                                                    {order.appliedOffer.code ||
                                                                                        order.appliedOffer.title}
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        {order.paymentDetails?.discount > 0 && (
                                                                            <div className="flex justify-between items-center">
                                                                                <span className="text-slate-500">
                                                                                    Discount:
                                                                                </span>
                                                                                <span className="font-semibold text-green-600">
                                                                                    -₹
                                                                                    {(
                                                                                        order.paymentDetails?.discount || 0
                                                                                    ).toLocaleString()}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex justify-between items-center mt-1">
                                                                            <span className="text-slate-500">
                                                                                Grand Total:
                                                                            </span>
                                                                            <span className="font-bold text-blue-700">
                                                                                ₹
                                                                                {(
                                                                                    order.paymentDetails?.totalAmount || 0
                                                                                ).toLocaleString()}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* RIGHT COLUMN: Products Table */}
                                                        <div className="lg:col-span-2">
                                                            <div className="bg-white p-3 rounded-none border border-slate-200/60 lg:h-full flex flex-col">
                                                                <p className="text-[10px] text-slate-400 uppercase font-bold mb-2">
                                                                    Ordered Items ({order.items?.length || 0})
                                                                </p>
                                                                <div className="overflow-x-auto flex-1">
                                                                    <table className="w-full text-[11px] text-left">
                                                                        <thead className="text-slate-400 uppercase tracking-wider font-bold border-b border-slate-100">
                                                                            <tr>
                                                                                <th className="pb-2 font-semibold">
                                                                                    Product
                                                                                </th>
                                                                                <th className="pb-2 text-center font-semibold">
                                                                                    Qty
                                                                                </th>
                                                                                <th className="pb-2 text-right font-semibold">
                                                                                    Unit Price
                                                                                </th>
                                                                                <th className="pb-2 text-right font-semibold">
                                                                                    Subtotal
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-slate-100">
                                                                            {order.items?.map((item, idx) => (
                                                                                <tr
                                                                                    key={idx}
                                                                                    className="hover:bg-slate-50 transition-colors"
                                                                                >
                                                                                    <td className="py-2.5 font-medium text-slate-700">
                                                                                        {item.productName}
                                                                                    </td>
                                                                                    <td className="py-2.5 text-center text-slate-600">
                                                                                        {item.quantity}{" "}
                                                                                        <span className="text-[9px] text-slate-400">
                                                                                            {item.quantityUnit || ""}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="py-2.5 text-right text-slate-600">
                                                                                        ₹
                                                                                        {(item.price || 0).toLocaleString()}
                                                                                    </td>
                                                                                    <td className="py-2.5 text-right font-bold text-slate-800">
                                                                                        ₹
                                                                                        {(
                                                                                            (item.price || 0) *
                                                                                            (item.quantity || 0)
                                                                                        ).toLocaleString()}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </ThemedTable>
                </div>
            </div>

            {/* ── PAGINATION ── */}
            <div className="border-t border-slate-200 py-4 flex justify-center bg-white">
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(p) => setPage(p)}
                />
            </div>

            <ConfirmDialog
                open={confirmModal.open}
                title={confirmModal.type === "cancel" ? "Cancel Order" : "Delete Order"}
                description={
                    confirmModal.type === "cancel"
                        ? "Are you sure you want to cancel this order? This action cannot be undone."
                        : "Are you sure you want to delete this order? All related data will be permanently removed."
                }
                variant="danger"
                confirmText={confirmModal.type === "cancel" ? "Yes, Cancel Order" : "Yes, Delete Order"}
                onConfirm={handleConfirmAction}
                onCancel={() => setConfirmModal({ open: false, type: null, orderId: null })}
            />
        </div>
    );
};

export default RetailOrders;
